import React, { useCallback, useState } from 'react'
import { BIBLE_BOOKS, OT_BOOKS, NT_BOOKS, UA_REF_MAP } from './bibleBooks'

const BIBLE_API = 'https://bible-api.com'
const BOLLS_API = 'https://bolls.life'

// Parse "Ів. 3:16", "john 3:16", "Пс. 23", "romans 8:28-39" etc.
function parseReference(raw, lang) {
  const s = raw.trim()

  // Try "Book Chapter:Verse[-Verse]" pattern
  const m = s.match(/^(.+?)\s+(\d+)(?::(\d+)(?:-(\d+))?)?\.?$/i)
  if (!m) return null

  const bookRaw = m[1].replace(/\.$/, '').toLowerCase().trim()
  const chapter = m[2]
  const verseStart = m[3]
  const verseEnd = m[4]

  // Find book
  let book = null

  // Try UA short names
  for (const [abbr, id] of Object.entries(UA_REF_MAP)) {
    if (bookRaw === abbr) { book = BIBLE_BOOKS.find(b => b.id === id); break }
  }

  // Try English/Ukrainian full name substring
  if (!book) {
    book = BIBLE_BOOKS.find(b =>
      b.en.toLowerCase().startsWith(bookRaw) ||
      b.ua.toLowerCase().startsWith(bookRaw) ||
      bookRaw.startsWith(b.id)
    )
  }

  if (!book) return null

  let ref = `${book.apiName}+${chapter}`
  if (verseStart) ref += `:${verseStart}`
  if (verseEnd) ref += `-${verseEnd}`

  return { book, chapter: Number(chapter), verseStart: verseStart || null, ref }
}

// ── Sub-components ──────────────────────────────────────────────────────────

const BookGrid = ({ books, label, lang, onBook }) => (
  <div style={s.testamentSection}>
    <div style={s.testamentLabel}>{label}</div>
    <div style={s.bookGrid}>
      {books.map(b => (
        <button key={b.id} style={s.bookBtn} onClick={() => onBook(b)}>
          {lang === 'en' ? b.en : b.ua}
        </button>
      ))}
    </div>
  </div>
)

const ChapterNav = ({ book, chapter, lang, onChapter, onBack }) => {
  const total = book.chapters
  return (
    <div style={s.chapterNav}>
      <button style={s.backBtn} onClick={onBack}>← {lang === 'en' ? 'Books' : 'Книги'}</button>
      <span style={s.breadcrumb}>{lang === 'en' ? book.en : book.ua}</span>
      <div style={s.chapterPills}>
        {Array.from({ length: total }, (_, i) => i + 1).map(n => (
          <button
            key={n}
            style={{ ...s.chapterPill, ...(n === chapter ? s.chapterPillActive : {}) }}
            onClick={() => onChapter(n)}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  )
}

// ── Main component ──────────────────────────────────────────────────────────

const BibleViewer = () => {
  const [lang, setLang] = useState(localStorage.getItem('userLanguage') === 'en' ? 'en' : 'ua')
  const [view, setView] = useState('toc')       // toc | chapter | search
  const [book, setBook] = useState(null)
  const [chapter, setChapter] = useState(1)
  const [verses, setVerses] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [query, setQuery] = useState('')
  const [searchResult, setSearchResult] = useState(null)
  const [highlight, setHighlight] = useState('')

  const fetchChapter = useCallback(async (b, ch) => {
    setLoading(true)
    setError(null)
    setVerses([])
    try {
      let verses
      if (lang === 'ua') {
        const res = await fetch(`${BOLLS_API}/get-chapter/UBIO/${b.bookNum}/${ch}/`)
        if (!res.ok) throw new Error()
        const data = await res.json()
        verses = data.map(v => ({ verse: v.verse, text: v.text }))
      } else {
        const res = await fetch(`${BIBLE_API}/${b.apiName}+${ch}?translation=web`)
        if (!res.ok) throw new Error()
        const data = await res.json()
        verses = data.verses || []
      }
      setVerses(verses)
      setBook(b)
      setChapter(ch)
      setView('chapter')
    } catch {
      setError(lang === 'en' ? 'Failed to load. Try again.' : 'Помилка завантаження. Спробуйте ще.')
    } finally {
      setLoading(false)
    }
  }, [lang])

  const handleSearch = useCallback(async (e) => {
    e.preventDefault()
    const q = query.trim()
    if (!q) return

    const parsed = parseReference(q, lang)
    if (parsed) {
      setHighlight(parsed.verseStart || '')
      await fetchChapter(parsed.book, parsed.chapter)
      if (parsed.verseStart) {
        setTimeout(() => {
          const el = document.getElementById(`verse-${parsed.verseStart}`)
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 400)
      }
      return
    }

    // Text search
    setLoading(true)
    setError(null)
    setSearchResult(null)
    try {
      let verses
      if (lang === 'ua') {
        const res = await fetch(`${BOLLS_API}/search/UBIO/?search=${encodeURIComponent(q)}`)
        if (!res.ok) throw new Error()
        const data = await res.json()
        verses = data.map(v => ({
          book_name: BIBLE_BOOKS.find(bk => bk.bookNum === v.book)?.[lang] || '',
          chapter: v.chapter,
          verse: v.verse,
          text: v.text.replace(/<\/?mark>/g, ''),
        }))
      } else {
        const res = await fetch(`${BIBLE_API}/${encodeURIComponent(q)}?translation=web`)
        if (!res.ok) throw new Error()
        const data = await res.json()
        verses = data.verses || []
      }
      if (verses.length) {
        setSearchResult({ q, verses })
        setView('search')
      } else {
        setError(lang === 'en' ? 'No results found.' : 'Нічого не знайдено.')
      }
    } catch {
      setError(lang === 'en'
        ? 'Enter a verse reference (e.g. "John 3:16") or book name.'
        : 'Введіть посилання (напр. "Ів. 3:16") або назву книги.')
    } finally {
      setLoading(false)
    }
  }, [query, lang, fetchChapter])

  const goToc = () => { setView('toc'); setBook(null); setVerses([]); setSearchResult(null); setError(null) }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={s.wrap}>

      {/* Toolbar */}
      <div style={s.toolbar}>
        <form onSubmit={handleSearch} style={s.searchForm}>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={lang === 'en'
              ? 'Search — e.g. "John 3:16" or "love"'
              : 'Пошук — напр. "Ів. 3:16" або "любов"'}
            style={s.searchInput}
          />
          <button type='submit' style={s.searchBtn} disabled={loading}>
            {loading ? '…' : '🔍'}
          </button>
        </form>

      </div>

      {error && <div style={s.errorBox}>{error}</div>}

      {/* TOC */}
      {view === 'toc' && (
        <div style={s.toc}>
          <BookGrid
            books={OT_BOOKS}
            label={lang === 'en' ? '📜 Old Testament' : '📜 Старий Заповіт'}
            lang={lang}
            onBook={b => fetchChapter(b, 1)}
          />
          <BookGrid
            books={NT_BOOKS}
            label={lang === 'en' ? '✝ New Testament' : '✝ Новий Заповіт'}
            lang={lang}
            onBook={b => fetchChapter(b, 1)}
          />
        </div>
      )}

      {/* Chapter */}
      {view === 'chapter' && book && (
        <div>
          <ChapterNav
            book={book}
            chapter={chapter}
            lang={lang}
            onChapter={n => fetchChapter(book, n)}
            onBack={goToc}
          />
          {loading
            ? <div style={s.loading}>{lang === 'en' ? 'Loading…' : 'Завантаження…'}</div>
            : (
              <div style={s.verseList}>
                {verses.map(v => (
                  <div
                    key={v.verse}
                    id={`verse-${v.verse}`}
                    style={{ ...s.verseRow, ...(String(v.verse) === highlight ? s.verseHighlight : {}) }}
                  >
                    <span style={s.verseNum}>{v.verse}</span>
                    <span style={s.verseText}>{v.text}</span>
                  </div>
                ))}
              </div>
            )
          }
          {/* Chapter navigation buttons */}
          {!loading && verses.length > 0 && (
            <div style={s.chapterFooter}>
              {chapter > 1 && (
                <button style={s.navFooterBtn} onClick={() => fetchChapter(book, chapter - 1)}>
                  ← {lang === 'en' ? 'Previous' : 'Попередня'}
                </button>
              )}
              {chapter < book.chapters && (
                <button style={{ ...s.navFooterBtn, marginLeft: 'auto' }} onClick={() => fetchChapter(book, chapter + 1)}>
                  {lang === 'en' ? 'Next' : 'Наступна'} →
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Search results */}
      {view === 'search' && searchResult && (
        <div>
          <div style={s.searchHeader}>
            <button style={s.backBtn} onClick={goToc}>← {lang === 'en' ? 'Books' : 'Книги'}</button>
            <span style={s.breadcrumb}>
              {lang === 'en' ? `Results for "${searchResult.q}"` : `Результати: "${searchResult.q}"`}
            </span>
          </div>
          <div style={s.verseList}>
            {searchResult.verses.map((v, i) => (
              <div key={i} style={s.verseRow}>
                <span
                  style={{ ...s.verseNum, ...s.refLink }}
                  onClick={() => {
                    const b = BIBLE_BOOKS.find(bk => bk.en === v.book_name || bk.ua === v.book_name)
                    if (b) fetchChapter(b, v.chapter)
                  }}
                >
                  {v.book_name} {v.chapter}:{v.verse}
                </span>
                <span style={s.verseText}>{v.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && view !== 'chapter' && (
        <div style={s.loading}>{lang === 'en' ? 'Loading…' : 'Завантаження…'}</div>
      )}
    </div>
  )
}

export default BibleViewer

// ── Styles ──────────────────────────────────────────────────────────────────
const s = {
  wrap: {
    fontFamily: '"Segoe UI", system-ui, sans-serif',
    color: '#2d1f14',
  },

  /* Toolbar */
  toolbar: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
  searchForm: {
    display: 'flex',
    flex: 1,
    minWidth: '200px',
    gap: '0',
  },
  searchInput: {
    flex: 1,
    padding: '10px 14px',
    border: '1.5px solid #e8d8c8',
    borderRight: 'none',
    borderRadius: '8px 0 0 8px',
    fontSize: '14px',
    outline: 'none',
    color: '#2d1f14',
  },
  searchBtn: {
    padding: '10px 16px',
    background: '#8b6044',
    color: '#fff',
    border: 'none',
    borderRadius: '0 8px 8px 0',
    cursor: 'pointer',
    fontSize: '16px',
  },
  langToggle: {
    display: 'flex',
    border: '1.5px solid #e8d8c8',
    borderRadius: '8px',
    overflow: 'hidden',
    flexShrink: 0,
  },
  langBtn: {
    padding: '9px 16px',
    border: 'none',
    background: '#fff',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    color: '#9a7c63',
  },
  langBtnActive: {
    background: '#8b6044',
    color: '#fff',
  },

  errorBox: {
    background: '#fdf4ec',
    border: '1px solid #e8d8c8',
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '14px',
    color: '#8b6044',
    marginBottom: '20px',
  },

  /* TOC */
  toc: { display: 'flex', flexDirection: 'column', gap: '28px' },
  testamentSection: {},
  testamentLabel: {
    fontSize: '13px',
    fontWeight: '700',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: '#9a7c63',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: '1px solid #f0e4d4',
  },
  bookGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
    gap: '8px',
  },
  bookBtn: {
    padding: '9px 10px',
    background: '#fdfaf7',
    border: '1.5px solid #f0e4d4',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#2d1f14',
    textAlign: 'left',
    transition: 'background 0.12s, border-color 0.12s',
    fontFamily: 'inherit',
  },

  /* Chapter nav */
  chapterNav: {
    marginBottom: '20px',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#8b6044',
    fontSize: '14px',
    fontWeight: '600',
    padding: '0',
    marginBottom: '10px',
    fontFamily: 'inherit',
  },
  breadcrumb: {
    display: 'block',
    fontSize: '20px',
    fontWeight: '700',
    color: '#2d1f14',
    marginBottom: '14px',
  },
  chapterPills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  chapterPill: {
    padding: '5px 10px',
    borderRadius: '6px',
    border: '1.5px solid #e8d8c8',
    background: '#fdfaf7',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#2d1f14',
    fontFamily: 'inherit',
    minWidth: '36px',
    textAlign: 'center',
  },
  chapterPillActive: {
    background: '#8b6044',
    borderColor: '#8b6044',
    color: '#fff',
    fontWeight: '700',
  },

  /* Verses */
  verseList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  verseRow: {
    display: 'flex',
    gap: '12px',
    padding: '7px 10px',
    borderRadius: '6px',
    alignItems: 'flex-start',
  },
  verseHighlight: {
    background: '#fdf4ec',
    border: '1px solid #c4986a',
  },
  verseNum: {
    flexShrink: 0,
    fontSize: '11px',
    fontWeight: '700',
    color: '#c4986a',
    minWidth: '24px',
    marginTop: '3px',
    textAlign: 'right',
  },
  verseText: {
    fontSize: '16px',
    lineHeight: '1.65',
    color: '#2d1f14',
  },
  refLink: {
    cursor: 'pointer',
    minWidth: '100px',
    fontSize: '12px',
    textDecoration: 'underline',
    color: '#8b6044',
  },

  /* Footer nav */
  chapterFooter: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
    paddingTop: '16px',
    borderTop: '1px solid #f0e4d4',
  },
  navFooterBtn: {
    padding: '10px 20px',
    background: 'transparent',
    border: '1.5px solid #c4986a',
    borderRadius: '8px',
    cursor: 'pointer',
    color: '#8b6044',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'inherit',
  },

  /* Search results header */
  searchHeader: {
    marginBottom: '20px',
  },

  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#9a7c63',
    fontSize: '16px',
  },
}
